import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Zap, CheckCircle, XCircle, Settings, RefreshCw, Key,
  Webhook, AlertTriangle, Wifi, WifiOff, Bot, Copy, Eye, EyeOff,
  MessageSquare, Activity, ExternalLink, ChevronRight, Info,
  Shield, ArrowRight, Play, Phone, Save, Terminal, Bug
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BRAIN_WEBHOOK_URL = 'https://okbot.okgo.mx/api/jelou/webhook/brain';

// Instancia axios con token de administrador (endpoints /admin/jelou/* requieren auth)
const api = axios.create();
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('okbot_admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ─── Helpers ───────────────────────────────────────────────────────────────
function CopyBtn({ value, label }) {
  const [ok, setOk] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(value); setOk(true); setTimeout(() => setOk(false), 1500); }}
      title={label || 'Copiar'}
      className="shrink-0 p-1.5 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 transition-colors"
    >
      {ok
        ? <CheckCircle size={13} className="text-green-400" />
        : <Copy size={13} className="text-gray-300" />}
    </button>
  );
}

function Field({ label, value, mono, secret }) {
  const [show, setShow] = useState(false);
  const display = secret && !show ? (value ? value.slice(0, 8) + '••••••••••••' : '') : value;
  return (
    <div className="flex items-center gap-2 bg-[#0d1117] rounded-lg px-3 py-2 border border-white/15">
      <span className="text-gray-400 text-xs w-24 shrink-0">{label}</span>
      <span className={`flex-1 text-xs truncate ${mono ? 'font-mono text-emerald-300' : 'text-white'}`}>{display || <em className="text-gray-600">vacío</em>}</span>
      {secret && value && (
        <button onClick={() => setShow(v => !v)} className="shrink-0 text-gray-500 hover:text-gray-300">
          {show ? <EyeOff size={12} /> : <Eye size={12} />}
        </button>
      )}
      {value && <CopyBtn value={value} />}
    </div>
  );
}

// ─── CONFIG SECTION ────────────────────────────────────────────────────────
function ConfigSection({ onSaved }) {
  const [form, setForm] = useState({
    brain_token: '', api_key: '', account_id: '',
    whatsapp_number: '', default_agent_id: 'main',
    auto_reply: true, is_active: true, max_history: 12,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showBt, setShowBt] = useState(false);
  const [showAk, setShowAk] = useState(false);

  useEffect(() => {
    api.get(`${API}/admin/jelou/config`).then(r => {
      if (r.data?.config) setForm(p => ({ ...p, ...r.data.config }));
      if (r.data?.configured) setSaved(true);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setSaved(false); };

  const genToken = () => {
    const t = Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map(b => b.toString(16).padStart(2, '0')).join('');
    set('brain_token', t);
    toast.info('Token generado — guarda la configuración');
  };

  const save = async () => {
    if (!form.brain_token?.trim()) { toast.error('El Brain Token es obligatorio'); return; }
    setSaving(true);
    try {
      await api.post(`${API}/admin/jelou/config`, form);
      toast.success('Configuración guardada ✓');
      setSaved(true);
      onSaved?.(form.brain_token);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-gray-400 text-sm animate-pulse py-4">Cargando…</div>;

  return (
    <div className="space-y-5">

      {saved && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-green-500/10 border border-green-500/30 text-green-300 text-sm">
          <CheckCircle size={15} />
          <span>Configuración guardada en base de datos</span>
        </div>
      )}

      {/* Brain Token */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Shield size={14} className="text-yellow-400" />
          <span className="text-sm font-semibold text-white">Brain Token</span>
          <span className="text-xs text-gray-400">(secreto que Jelou envía en X-Token)</span>
        </div>
        <div className="p-3 rounded-lg bg-blue-950/50 border border-blue-500/20 text-xs text-blue-300">
          Jelou envía este token en el header <code className="bg-black/40 px-1 rounded">X-Token</code> para autenticar cada mensaje.
          Es diferente al API Key outbound.
        </div>
        <button onClick={genToken}
          className="w-full py-2 rounded-lg bg-yellow-500/15 hover:bg-yellow-500/25 border border-yellow-500/30 text-yellow-300 text-sm font-medium transition-colors flex items-center justify-center gap-2">
          <Key size={14} /> Generar Brain Token automáticamente
        </button>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type={showBt ? 'text' : 'password'}
              value={form.brain_token}
              onChange={e => set('brain_token', e.target.value)}
              placeholder="sk_... o cualquier secreto compartido"
              className="w-full bg-[#0d1117] border border-white/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 pr-10 focus:outline-none focus:border-yellow-500/60"
            />
            <button onClick={() => setShowBt(v => !v)} className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-300">
              {showBt ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          {form.brain_token && <CopyBtn value={form.brain_token} />}
        </div>
      </div>

      {/* API Key Outbound */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Key size={14} className="text-blue-400" />
          <span className="text-sm font-semibold text-white">API Key Outbound</span>
          <span className="text-xs text-gray-400">(para enviar mensajes proactivos)</span>
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type={showAk ? 'text' : 'password'}
              value={form.api_key}
              onChange={e => set('api_key', e.target.value)}
              placeholder="sk_... (API Key REST de Jelou — opcional)"
              className="w-full bg-[#0d1117] border border-white/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 pr-10 focus:outline-none focus:border-blue-500/60"
            />
            <button onClick={() => setShowAk(v => !v)} className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-300">
              {showAk ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          {form.api_key && <CopyBtn value={form.api_key} />}
        </div>
      </div>

      {/* Account ID */}
      <div className="space-y-2">
        <label className="text-sm font-semibold text-white flex items-center gap-2">
          <Activity size={14} className="text-purple-400" /> Account ID de Jelou
        </label>
        <input
          type="text"
          value={form.account_id}
          onChange={e => set('account_id', e.target.value)}
          placeholder="ej: 56e6ca13-062c-439a-b647-c0f2c4a9f464"
          className="w-full bg-[#0d1117] border border-white/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-purple-500/60"
        />
      </div>

      {/* Número WhatsApp */}
      <div className="space-y-2">
        <label className="text-sm font-semibold text-white flex items-center gap-2">
          <Phone size={14} className="text-green-400" /> Número WhatsApp de Jelou
        </label>
        <input
          type="tel"
          value={form.whatsapp_number}
          onChange={e => set('whatsapp_number', e.target.value)}
          placeholder="ej: +15559454330"
          className="w-full bg-[#0d1117] border border-white/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-green-500/60"
        />
        {form.whatsapp_number && (
          <a href={`https://wa.me/${form.whatsapp_number.replace(/\D/g,'')}`} target="_blank" rel="noreferrer"
            className="text-xs text-green-400 hover:underline flex items-center gap-1">
            <ExternalLink size={10} /> Probar enlace wa.me
          </a>
        )}
      </div>

      {/* Switches */}
      <div className="flex gap-6">
        {[['auto_reply', 'Respuesta automática'], ['is_active', 'Brain activo']].map(([k, label]) => (
          <label key={k} className="flex items-center gap-2 cursor-pointer">
            <div onClick={() => set(k, !form[k])}
              className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${form[k] ? 'bg-yellow-500' : 'bg-white/15'}`}>
              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${form[k] ? 'left-5' : 'left-0.5'}`} />
            </div>
            <span className="text-sm text-gray-200">{label}</span>
          </label>
        ))}
      </div>

      {/* Historial */}
      <div className="space-y-1">
        <label className="text-xs text-gray-300 font-medium">Historial de conversación: {form.max_history} mensajes</label>
        <input type="range" min={4} max={40} step={2} value={form.max_history}
          onChange={e => set('max_history', parseInt(e.target.value))}
          className="w-full accent-yellow-400" />
        <div className="flex justify-between text-xs text-gray-500"><span>4 (rápido)</span><span>40 (máximo)</span></div>
      </div>

      <button onClick={save} disabled={saving}
        className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 disabled:bg-yellow-500/50 text-black font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2">
        <Save size={15} /> {saving ? 'Guardando…' : 'Guardar configuración'}
      </button>
    </div>
  );
}

// ─── BRAIN TEST SECTION ────────────────────────────────────────────────────
function BrainTestSection() {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [simPhone, setSimPhone] = useState('+521234567890');
  const [simMsg, setSimMsg] = useState('Hola Jarvis, ¿me puedes ayudar con mis tareas de hoy?');
  const [simming, setSimming] = useState(false);
  const [simResult, setSimResult] = useState(null);

  const runSelfTest = async () => {
    setTesting(true); setResult(null);
    try {
      const r = await api.post(`${API}/admin/jelou/brain-self-test`);
      setResult({ ok: true, reply: r.data.jarvis_reply });
    } catch (e) {
      setResult({ ok: false, error: e.response?.data?.detail || 'Error de conexión' });
    } finally { setTesting(false); }
  };

  const runSimulation = async () => {
    if (!simPhone.trim() || !simMsg.trim()) return;
    setSimming(true); setSimResult(null);
    try {
      const r = await api.post(`${API}/admin/jelou/simulate-incoming`, {
        phone: simPhone.trim(),
        message: simMsg.trim(),
        name: 'Admin Sim',
      });
      setSimResult({ ok: true, data: r.data });
    } catch (e) {
      setSimResult({ ok: false, error: e.response?.data?.detail || 'Error' });
    } finally { setSimming(false); }
  };

  return (
    <div className="space-y-5">

      {/* PASO 1: Test interno del Brain */}
      <div className="p-4 rounded-xl bg-[#0d1117] border border-yellow-500/30 space-y-3">
        <p className="text-sm font-bold text-yellow-300 flex items-center gap-2">
          <Terminal size={15} /> Test 1 — Verificar que Jarvis responde
        </p>
        <p className="text-xs text-gray-400">
          Llama directamente al motor de IA sin pasar por HTTP ni Jelou.
          Confirma que OpenRouter y el agentic loop de Jarvis funcionan.
        </p>
      </div>

      <button onClick={runSelfTest} disabled={testing}
        className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 disabled:bg-yellow-500/50 text-black font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2">
        {testing
          ? <><RefreshCw size={14} className="animate-spin" /> Consultando a Jarvis… (~15s)</>
          : <><Play size={14} /> Probar motor de Jarvis (sin Jelou)</>}
      </button>

      {result && (
        <div className={`p-4 rounded-xl border ${result.ok ? 'bg-green-950/50 border-green-500/30' : 'bg-red-950/50 border-red-500/30'}`}>
          {result.ok ? (
            <>
              <p className="text-sm font-bold text-green-300 flex items-center gap-2 mb-2"><CheckCircle size={14} /> ✅ Jarvis OK</p>
              <div className="bg-[#0d1117] rounded-lg p-3 border border-white/10">
                <p className="text-xs text-gray-500 mb-1">Respuesta de Jarvis:</p>
                <p className="text-sm text-gray-100 leading-relaxed">{result.reply}</p>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm font-bold text-red-300 flex items-center gap-2 mb-2"><XCircle size={14} /> Error</p>
              <p className="text-xs text-red-300">{result.error}</p>
            </>
          )}
        </div>
      )}

      <div className="border-t border-white/10 pt-5">
        {/* PASO 2: Simular mensaje WA completo */}
        <div className="p-4 rounded-xl bg-[#0d1117] border border-blue-500/30 space-y-3 mb-4">
          <p className="text-sm font-bold text-blue-300 flex items-center gap-2">
            <MessageSquare size={15} /> Test 2 — Simular mensaje WA completo
          </p>
          <p className="text-xs text-gray-400">
            Simula exactamente lo que Jelou enviaría cuando un usuario escribe por WhatsApp.
            Prueba el pipeline completo: mensaje → verificación → Jarvis → respuesta.
          </p>
        </div>

        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs text-gray-400 font-medium">Teléfono del usuario (como lo envía Jelou)</label>
            <input
              value={simPhone} onChange={e => setSimPhone(e.target.value)}
              className="w-full bg-[#0d1117] border border-white/20 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500/60"
              placeholder="+521234567890"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-gray-400 font-medium">Mensaje del usuario</label>
            <textarea
              value={simMsg} onChange={e => setSimMsg(e.target.value)}
              rows={3}
              className="w-full bg-[#0d1117] border border-white/20 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500/60 resize-none"
              placeholder="Escribe el mensaje del usuario..."
            />
          </div>
          <p className="text-xs text-gray-600">
            💡 Tip: escribe un código de 6 dígitos para probar la vinculación de cuenta
          </p>

          <button onClick={runSimulation} disabled={simming}
            className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-600/50 text-white font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2">
            {simming
              ? <><RefreshCw size={14} className="animate-spin" /> Procesando mensaje (~15s)…</>
              : <><Play size={14} /> Simular mensaje entrante de WhatsApp</>}
          </button>
        </div>

        {simResult && (
          <div className={`mt-4 p-4 rounded-xl border ${simResult.ok ? 'bg-blue-950/40 border-blue-500/30' : 'bg-red-950/40 border-red-500/30'}`}>
            {simResult.ok ? (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle size={14} className="text-green-400" />
                  <p className="text-sm font-bold text-white">
                    {simResult.data.type === 'verification' ? '🔗 Código de verificación procesado' : '🤖 Jarvis respondió'}
                  </p>
                  <span className="text-xs text-gray-500 ml-auto">sesión: {simResult.data.session_id}</span>
                </div>
                <div className="bg-[#0d1117] rounded-lg p-3 border border-white/10 space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="text-xs text-gray-500 w-16 shrink-0">📲 Usuario:</span>
                    <p className="text-xs text-gray-300">{simMsg}</p>
                  </div>
                  <div className="border-t border-white/5 pt-2 flex items-start gap-2">
                    <span className="text-xs text-gray-500 w-16 shrink-0">🤖 Jarvis:</span>
                    <p className="text-sm text-gray-100 leading-relaxed">{simResult.data.reply}</p>
                  </div>
                </div>
                {simResult.data.format_jelou && (
                  <div className="mt-3 bg-[#0d1117] rounded-lg p-2 border border-white/5">
                    <p className="text-xs text-gray-600 mb-1">Formato que Jelou enviaría al usuario:</p>
                    <code className="text-xs text-green-300">{JSON.stringify(simResult.data.format_jelou)}</code>
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="text-sm font-bold text-red-300 flex items-center gap-2 mb-2"><XCircle size={14} /> Error</p>
                <p className="text-xs text-red-300">{simResult.error}</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── INSTRUCCIONES SECTION ─────────────────────────────────────────────────
function InstructionsSection({ brainToken }) {
  const [copied, setCopied] = useState(false);

  const TOKEN = brainToken || 'sk_5FMpXa3RIQ0tcSxSCZIw1O2QSmVsb3VBSQSF9qRG93QQLDLeZ8GdfLUgim';

  const text = `INSTRUCCIONES PARA CONFIGURAR OKBOT JARVIS BRAIN EN JELOU
==========================================================

Necesito configurar un Brain HTTP en Jelou para conectarlo con OkBot Jarvis.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PASO 1 — CREAR EL BRAIN HTTP
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
1. app.jelou.ai → menú lateral → Brains
2. "Nuevo Brain" → Tipo: HTTP
3. Nombre: OkBot Jarvis

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PASO 2 — CONFIGURAR EL ENDPOINT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
URL: ${BRAIN_WEBHOOK_URL}
Método: POST
Timeout: 30 segundos

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PASO 3 — AUTENTICACIÓN (HEADER)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Nombre del header: X-Token
Valor del header: ${TOKEN}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PASO 4 — ASIGNAR AL CANAL DE WHATSAPP
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Canales → tu número WA (+1 555 945 4330)
→ sección "Brain" → seleccionar "OkBot Jarvis" → Guardar

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
PASO 5 — VERIFICAR
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Enviar "Hola" al número de WhatsApp.
OkBot Jarvis responderá en 5-15 segundos.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
FORMATO DE RESPUESTA
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Jelou envía: { "userId":"...", "sessionId":"...", "message":"..." }
OkBot responde: { "outputs": [{ "message": "respuesta de Jarvis" }] }

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
SOLUCIÓN DE PROBLEMAS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
- El endpoint SIEMPRE devuelve HTTP 200 (nunca 401/500)
- Si no responde: verificar que el Brain esté asignado al canal WA
- URL exacta: ${BRAIN_WEBHOOK_URL}
`;

  const copy = () => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 3000); };

  const steps = [
    { n: 1, title: 'Crear Brain HTTP', color: 'blue', rows: [
      { l: 'Ruta', v: 'app.jelou.ai → Brains → Nuevo Brain', copy: false },
      { l: 'Tipo', v: 'HTTP', copy: true },
      { l: 'Nombre', v: 'OkBot Jarvis', copy: true },
    ]},
    { n: 2, title: 'Configurar Endpoint', color: 'green', rows: [
      { l: 'URL', v: BRAIN_WEBHOOK_URL, copy: true },
      { l: 'Método', v: 'POST', copy: true },
      { l: 'Timeout', v: '30 segundos', copy: false },
    ]},
    { n: 3, title: 'Autenticación Header', color: 'purple', rows: [
      { l: 'Header', v: 'X-Token', copy: true },
      { l: 'Valor', v: TOKEN, copy: true, secret: true },
    ]},
    { n: 4, title: 'Asignar al Canal WA', color: 'yellow', rows: [
      { l: 'Ruta', v: 'Canales → tu número WA', copy: false },
      { l: 'Sección', v: 'Brain', copy: false },
      { l: 'Elegir', v: 'OkBot Jarvis → Guardar', copy: false },
    ]},
  ];

  const colors = {
    blue:   'border-blue-500/30 bg-blue-950/30',
    green:  'border-green-500/30 bg-green-950/30',
    purple: 'border-purple-500/30 bg-purple-950/30',
    yellow: 'border-yellow-500/30 bg-yellow-950/30',
  };
  const badges = {
    blue: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
    green: 'bg-green-500/20 text-green-300 border-green-500/40',
    purple: 'bg-purple-500/20 text-purple-300 border-purple-500/40',
    yellow: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40',
  };
  const labels = { blue: 'text-blue-400', green: 'text-green-400', purple: 'text-purple-400', yellow: 'text-yellow-400' };

  const [showSecret, setShowSecret] = useState(false);

  return (
    <div className="space-y-5">

      <div className="flex items-center justify-between flex-wrap gap-3 p-4 rounded-xl bg-[#0d1117] border border-yellow-500/30">
        <div>
          <p className="text-sm font-bold text-white">Instrucciones completas para Jelou</p>
          <p className="text-xs text-gray-400 mt-0.5">Copia y pega en el soporte o IA de Jelou</p>
        </div>
        <button onClick={copy} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors ${copied ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'bg-yellow-500 text-black hover:bg-yellow-400'}`}>
          {copied ? <><CheckCircle size={14} /> ¡Copiado!</> : <><Copy size={14} /> Copiar todo</>}
        </button>
      </div>

      <div className="space-y-3">
        {steps.map(s => (
          <div key={s.n} className={`rounded-xl border overflow-hidden ${colors[s.color]}`}>
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-white/5">
              <div className={`w-6 h-6 rounded-full border flex items-center justify-center text-xs font-bold shrink-0 ${badges[s.color]}`}>{s.n}</div>
              <span className="text-sm font-bold text-white">{s.title}</span>
            </div>
            <div className="p-4 space-y-2">
              {s.rows.map(row => (
                <div key={row.l} className="flex items-start gap-3">
                  <span className={`text-xs font-semibold w-14 shrink-0 pt-0.5 ${labels[s.color]}`}>{row.l}:</span>
                  <div className="flex items-center gap-2 flex-1 min-w-0 bg-[#0d1117] rounded-lg px-2.5 py-1.5 border border-white/10">
                    <code className="text-xs text-gray-100 flex-1 break-all">
                      {row.secret && !showSecret ? row.v.slice(0, 8) + '••••••••••••' : row.v}
                    </code>
                    {row.secret && (
                      <button onClick={() => setShowSecret(v => !v)} className="text-gray-500 hover:text-gray-300 shrink-0">
                        {showSecret ? <EyeOff size={11} /> : <Eye size={11} />}
                      </button>
                    )}
                    {row.copy && <CopyBtn value={row.secret ? row.v : row.v} />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-white/15 overflow-hidden bg-[#0d1117]">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10 bg-white/5">
          <span className="text-xs font-semibold text-gray-300">Texto completo para pegar</span>
          <button onClick={copy} className={`flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors ${copied ? 'bg-green-500/15 text-green-300' : 'bg-white/10 text-gray-300 hover:bg-white/20'}`}>
            <Copy size={10} /> {copied ? 'Copiado' : 'Copiar'}
          </button>
        </div>
        <pre className="p-4 text-xs text-gray-300 leading-relaxed whitespace-pre-wrap font-mono max-h-56 overflow-y-auto">{text}</pre>
      </div>
    </div>
  );
}

// ─── CONVERSACIONES SECTION ────────────────────────────────────────────────
function ConversationsSection() {
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [debugLog, setDebugLog] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, d] = await Promise.all([
        api.get(`${API}/admin/jelou/conversations?limit=30`),
        api.get(`${API}/admin/jelou/debug-log`).catch(() => ({ data: { logs: [] } })),
      ]);
      setConvs(c.data.conversations || []);
      setDebugLog(d.data.logs || []);
    } catch { } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-xs text-gray-500 animate-pulse py-4">Cargando…</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-white">Conversaciones procesadas</p>
        <button onClick={load} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/15 border border-white/20 rounded-lg text-xs text-gray-300 transition-colors">
          <RefreshCw size={11} /> Actualizar
        </button>
      </div>

      {convs.length === 0 ? (
        <div className="text-center py-8">
          <MessageSquare size={32} className="text-gray-700 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Sin conversaciones aún</p>
          <p className="text-xs text-gray-600 mt-1">Los mensajes de Jelou aparecerán aquí cuando el Brain esté configurado</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {Object.entries(
            convs.reduce((a, m) => { const k = m.session_id || m.phone || '?'; if (!a[k]) a[k] = []; a[k].push(m); return a; }, {})
          ).slice(0, 10).map(([sid, msgs]) => (
            <div key={sid} className="p-3 rounded-xl bg-[#0d1117] border border-white/15">
              <div className="flex justify-between mb-2">
                <span className="text-xs text-gray-400 font-mono truncate max-w-[200px]">{sid}</span>
                <span className="text-xs text-gray-600">{msgs.length} msg</span>
              </div>
              {msgs.slice(-2).map((m, i) => (
                <div key={i} className={`text-xs py-1.5 px-2 rounded mb-1 ${m.role === 'assistant' ? 'bg-yellow-500/10 text-yellow-200' : 'bg-white/5 text-gray-300'}`}>
                  {m.role === 'assistant' ? '🤖 ' : '👤 '}{(m.content || '').slice(0, 120)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {debugLog.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-orange-300 flex items-center gap-1.5"><Bug size={12} /> Intentos de webhook capturados</p>
          {debugLog.slice(0, 5).map((d, i) => (
            <div key={i} className="p-3 rounded-xl bg-orange-950/30 border border-orange-500/25 text-xs text-gray-300">
              <p className="font-mono text-orange-300 mb-1">{d.method} {d.url}</p>
              <p className="text-gray-500 truncate">{d.body?.slice(0, 100)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── STATUS SECTION ────────────────────────────────────────────────────────
function StatusBar() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`${API}/admin/jelou/status`); setStatus(r.data); }
    catch (e) { setStatus({ status: 'error', message: 'Error de conexión' }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { check(); }, [check]);

  const colors = {
    connected: 'bg-green-500/10 border-green-500/30 text-green-300',
    configured: 'bg-green-500/10 border-green-500/30 text-green-300',
    not_configured: 'bg-gray-500/10 border-gray-500/30 text-gray-400',
    auth_error: 'bg-red-500/10 border-red-500/30 text-red-300',
    error: 'bg-red-500/10 border-red-500/30 text-red-300',
  };
  const icons = {
    connected: <Wifi size={13} />, configured: <CheckCircle size={13} />,
    not_configured: <WifiOff size={13} />, auth_error: <XCircle size={13} />, error: <XCircle size={13} />,
  };

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {status && (
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium ${colors[status.status] || colors.not_configured}`}>
          {icons[status.status] || <AlertTriangle size={13} />}
          <span>{status.message}</span>
        </div>
      )}
      <button onClick={check} disabled={loading}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/8 hover:bg-white/12 border border-white/20 rounded-lg text-xs text-gray-300 transition-colors disabled:opacity-50">
        <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Estado
      </button>
    </div>
  );
}

// ─── CANAL DIRECTO META ─────────────────────────────────────────────────────
// Los mensajes de WhatsApp llegan directo de Meta a OkBot (override del
// callback del WABA) — sin depender del enrutamiento interno de Jelou.
function MetaDirectSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`${API}/admin/meta/webhook/status`);
      setStatus(r.data);
    } catch { setStatus(null); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (enable) => {
    setLoading(true); setMsg('');
    try {
      const r = await api.post(`${API}/admin/meta/webhook/${enable ? 'enable' : 'disable'}`);
      if (enable) {
        setMsg(r.data?.override_active
          ? '✅ Canal directo ACTIVO — los mensajes llegan directo a OkBot'
          : '⏳ Activado. Meta se conectará automáticamente tras publicar la app (el sistema reintenta cada 10 min).');
      } else {
        setMsg('Canal directo desactivado — webhooks vuelven a Jelou.');
      }
      await load();
    } catch (e) {
      setMsg(`Error: ${e.response?.data?.detail || e.message}`);
    }
    setLoading(false);
  };

  const active = status?.override_active;
  const enabled = status?.enabled;

  return (
    <div className={`p-3 rounded-xl bg-[#0d1117] border ${active ? 'border-green-500/40' : 'border-blue-500/30'}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Shield size={13} className={active ? 'text-green-400' : 'text-blue-400'} />
          <span className="text-xs font-semibold text-white">Canal directo Meta → OkBot</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
            active ? 'bg-green-500/15 text-green-300 border-green-500/30'
                   : enabled ? 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30'
                             : 'bg-gray-500/15 text-gray-400 border-gray-500/30'}`}>
            {active ? 'ACTIVO' : enabled ? 'PENDIENTE (auto-conexión)' : 'INACTIVO'}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={load} disabled={loading}
            className="p-1.5 bg-white/8 hover:bg-white/12 border border-white/20 rounded-lg text-gray-300 disabled:opacity-50">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
          </button>
          {enabled ? (
            <button onClick={() => toggle(false)} disabled={loading}
              className="px-3 py-1.5 bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 rounded-lg text-xs text-red-300 disabled:opacity-50">
              Desactivar
            </button>
          ) : (
            <button onClick={() => toggle(true)} disabled={loading}
              className="px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/40 rounded-lg text-xs text-blue-300 font-semibold disabled:opacity-50">
              Activar canal directo
            </button>
          )}
        </div>
      </div>
      <p className="text-[11px] text-gray-500 mt-2">
        Los mensajes de WhatsApp llegan <b>directo de Meta a OkBot</b>, sin depender del
        enrutamiento interno de Jelou. Auto-reparación cada 10 min si algo lo revierte.
        {status?.messages_received > 0 && <> · <span className="text-gray-400">{status.messages_received} mensajes recibidos por este canal</span></>}
      </p>
      {msg && <p className="text-[11px] text-blue-300 mt-1.5">{msg}</p>}
    </div>
  );
}

// ─── MAIN PANEL ────────────────────────────────────────────────────────────
const JelouPanel = () => {
  const [tab, setTab] = useState('test');
  const [brainToken, setBrainToken] = useState('');

  useEffect(() => {
    api.get(`${API}/admin/jelou/config`).then(r => {
      if (r.data?.config?.brain_token) setBrainToken(r.data.config.brain_token);
    }).catch(() => {});
  }, []);

  const tabs = [
    { id: 'test',         label: '🧪 Probar',        icon: <Play size={13} /> },
    { id: 'instructions', label: '📋 Instrucciones', icon: <Info size={13} /> },
    { id: 'setup',        label: '⚙️ Configurar',    icon: <Settings size={13} /> },
    { id: 'convs',        label: '💬 Conversaciones', icon: <MessageSquare size={13} /> },
  ];

  return (
    <div className="max-w-2xl mx-auto p-5 space-y-5">

      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-2xl bg-yellow-500/15 border border-yellow-500/30 flex items-center justify-center shrink-0">
          <Zap size={20} className="text-yellow-400" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-lg font-bold text-white">Jelou — OkBot Brain</h1>
            <a href="https://docs.jelou.ai/" target="_blank" rel="noreferrer"
              className="text-xs text-yellow-400 hover:underline flex items-center gap-1">
              docs.jelou.ai <ExternalLink size={9} />
            </a>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Jelou recibe los mensajes de WhatsApp · Jarvis genera las respuestas con 45 herramientas
          </p>
        </div>
      </div>

      {/* Flujo visual */}
      <div className="flex items-center gap-2 p-3 rounded-xl bg-[#0d1117] border border-white/10 text-xs text-gray-300 overflow-x-auto">
        {[
          { icon: <MessageSquare size={11} className="text-green-400" />, label: 'WhatsApp usuario', color: 'bg-green-500/10 border-green-500/20' },
          { arrow: true },
          { icon: <Zap size={11} className="text-yellow-400" />, label: 'Jelou BSP', color: 'bg-yellow-500/10 border-yellow-500/20' },
          { arrow: true },
          { icon: <Bot size={11} className="text-blue-400" />, label: 'OkBot Brain', color: 'bg-blue-500/10 border-blue-500/20' },
          { arrow: true },
          { icon: <Activity size={11} className="text-purple-400" />, label: 'Jarvis + 45 tools', color: 'bg-purple-500/10 border-purple-500/20' },
        ].map((item, i) => item.arrow
          ? <ArrowRight key={i} size={12} className="text-gray-600 shrink-0" />
          : <div key={i} className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 border shrink-0 ${item.color}`}>{item.icon}<span>{item.label}</span></div>
        )}
      </div>

      {/* URL del Webhook — siempre visible */}
      <div className="p-3 rounded-xl bg-[#0d1117] border border-emerald-500/30">
        <div className="flex items-center gap-2 mb-2">
          <Webhook size={13} className="text-emerald-400" />
          <span className="text-xs font-semibold text-emerald-300">Brain Webhook URL (pegar en Jelou)</span>
        </div>
        <div className="flex items-center gap-2 bg-black/40 rounded-lg px-3 py-2 border border-white/10">
          <code className="text-xs text-emerald-300 flex-1 break-all">{BRAIN_WEBHOOK_URL}</code>
          <CopyBtn value={BRAIN_WEBHOOK_URL} />
        </div>
      </div>

      {/* Canal directo Meta — solución definitiva de entrega de mensajes */}
      <MetaDirectSection />

      {/* Status */}
      <StatusBar />

      {/* Tabs */}
      <div className="flex gap-1 bg-[#0d1117] rounded-xl p-1 border border-white/10">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs transition-colors flex-1 justify-center font-medium
              ${tab === t.id ? 'bg-yellow-500 text-black font-bold' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="bg-[#0d1117] border border-white/10 rounded-2xl p-5">
        {tab === 'test'         && <BrainTestSection />}
        {tab === 'instructions' && <InstructionsSection brainToken={brainToken} />}
        {tab === 'setup'        && <ConfigSection onSaved={tok => { if (tok) setBrainToken(tok); }} />}
        {tab === 'convs'        && <ConversationsSection />}
      </div>

      {/* Footer */}
      <div className="flex gap-3 p-3 rounded-xl bg-[#0d1117] border border-white/10 text-xs text-gray-500">
        <Shield size={13} className="shrink-0 mt-0.5 text-gray-600" />
        <p>
          Mensajes autenticados con Brain Token (HMAC). Historial en{' '}
          <code className="text-gray-400">jelou_conversations</code>.
          Debug log en <code className="text-gray-400">jelou_debug_log</code>.
        </p>
      </div>
    </div>
  );
};

export default JelouPanel;
